import React from 'react';
import { Box } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Control } from 'react-hook-form';
import { NamingConfig } from '../../../../types/packager-types';
import NamingElementRow from './NamingElementRow';

interface NamingElementsListProps {
  fields: any[];
  control: Control<any>;
  errors: any;
  configPath: 'processed_data_naming_config' | 'source_file_naming_config';
  watchedConfig: NamingConfig;
  metadataOptions: Array<{ value: string; label: string }>;
  documentDataOptions: Array<{ value: string; label: string }>;
  onRemove: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  isLoadingMetadata?: boolean;
}

const NamingElementsList: React.FC<NamingElementsListProps> = ({
  fields,
  control,
  errors,
  configPath,
  watchedConfig,
  metadataOptions,
  documentDataOptions,
  onRemove,
  onMove,
  isLoadingMetadata = false,
}) => {
  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (!result.destination) return;
        onMove(result.source.index, result.destination.index);
      }}
    >
      <Droppable
        droppableId={`${configPath}-naming-elements-droppable`}
        renderClone={(provided, snapshot, rubric) => {
          const index = rubric.source.index;
          const field = fields[index];
          return (
            <NamingElementRow
              element={watchedConfig.elements?.[index] || { type: 'STRING_LITERAL', value: '' }}
              index={index}
              field={field}
              control={control}
              errors={errors}
              configPath={configPath}
              watchedConfig={watchedConfig}
              metadataOptions={metadataOptions}
              documentDataOptions={documentDataOptions}
              onRemove={onRemove}
              isLoadingMetadata={isLoadingMetadata}
              isDragging={true}
              dragHandleProps={provided.dragHandleProps}
              draggableProps={provided.draggableProps}
              innerRef={provided.innerRef}
            />
          );
        }}
      >
        {(provided) => {
          return (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                mb: 2,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'grey.50',
              }}
            >
              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index}>
                  {(provided, snapshot) => (
                    <NamingElementRow
                      element={
                        watchedConfig.elements?.[index] || { type: 'STRING_LITERAL', value: '' }
                      }
                      index={index}
                      field={field}
                      control={control}
                      errors={errors}
                      configPath={configPath}
                      watchedConfig={watchedConfig}
                      metadataOptions={metadataOptions}
                      documentDataOptions={documentDataOptions}
                      onRemove={onRemove}
                      isLoadingMetadata={isLoadingMetadata}
                      isDragging={snapshot.isDragging}
                      dragHandleProps={provided.dragHandleProps}
                      draggableProps={provided.draggableProps}
                      innerRef={provided.innerRef}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          );
        }}
      </Droppable>
    </DragDropContext>
  );
};

export default NamingElementsList;
